import { Component } from "react";
import type { ReactNode } from "react";
import * as Sentry from "@sentry/react";
import { Button } from "@/components/ui/button";
import { captureException } from "@/lib/posthog";

type SectionErrorBoundaryProps = {
    children: ReactNode;
    sectionName?: string;
};

type SectionErrorBoundaryState = {
    hasError: boolean;
    error: unknown;
};

export class SectionErrorBoundary extends Component<
    SectionErrorBoundaryProps,
    SectionErrorBoundaryState
> {
    state: SectionErrorBoundaryState = { hasError: false, error: null };

    static getDerivedStateFromError(error: unknown): Partial<SectionErrorBoundaryState> {
        return { hasError: true, error };
    }

    componentDidCatch(error: unknown) {
        const sectionLabel = this.props.sectionName ?? "Section";
        console.error(`${sectionLabel} crashed:`, error);
        Sentry.captureException(error, {
            tags: { section: sectionLabel },
        });
        captureException(error, { section: sectionLabel });
    }

    handleReload = () => {
        window.location.reload();
    };

    static isChunkLoadError(error: unknown): boolean {
        const message = error instanceof Error ? error.message : String(error);
        return (
            message.includes("Failed to fetch dynamically imported module") ||
            message.includes("Loading chunk") ||
            message.includes("Importing a module script failed")
        );
    }

    render() {
        if (this.state.hasError) {
            const isChunkError = this.state.error != null && SectionErrorBoundary.isChunkLoadError(this.state.error);
            const heading = this.props.sectionName
                ? `${this.props.sectionName} hit an error.`
                : "This section hit an error.";
            const description = isChunkError
                ? "This often happens after we update the app. Refreshing the page usually fixes it."
                : "Try reloading this view. If the problem persists, check the console for details.";

            return (
                <div className="flex min-h-[40vh] w-full flex-col items-center justify-center gap-3 p-6">
                    <h2 className="text-lg font-semibold">{heading}</h2>
                    <p className="text-sm text-muted-foreground text-center max-w-md">
                        {description}
                    </p>
                    <Button onClick={this.handleReload} data-testid="button-reload-section">
                        {isChunkError ? "Refresh page" : "Reload"}
                    </Button>
                </div>
            );
        }

        return this.props.children;
    }
}
